// Section for import npm components -----------------------------------------------------------------------------------
import React, { useState, useEffect, useContext } from 'react';

// MUI components
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';

// load navigation contents from JSON file
import headerNavigationItemsProduction from '@config/data/headerNavigationItems.json';
import headerNavigationItemsStaging from '@config/data/headerNavigationItems.staging.json';
import headerNavigationItemsDevelopment from '@config/data/headerNavigationItems.development.json';

// Section for import custom components --------------------------------------------------------------------------------
import CommonUtility from '@utils/Common';
import { AppConfigContext } from '@providers/AppConfigProvider';

export default function GlobalNavigation() {
  const [currentMenuIndex, setCurrentMenuIndex] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isListHovered, setIsListHovered] = useState<boolean>(false);
  const { appEnv } = useContext(AppConfigContext);

  const isActive = (url: string): boolean => {
    return window.location.href === url;
  };

  // 環境変数によって読み込むjsonファイルを変更する
  const headerNavigationItems = CommonUtility.getEnvBasedValue(
    {
      production: headerNavigationItemsProduction,
      staging: headerNavigationItemsStaging,
      development: headerNavigationItemsDevelopment,
    },
    appEnv,
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
    window.location.href = headerNavigationItems[index].href;
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, index: number) => {
    if (headerNavigationItems[index].children) {
      setAnchorEl(event.currentTarget);
      setCurrentMenuIndex(index);
    }
  };

  useEffect(() => {
    if (!isHovered && !isListHovered) {
      setAnchorEl(null);
      setCurrentMenuIndex(null);
    }
  }, [isHovered, isListHovered]);

  // estate-karteのキーを持つアイテムを除外
  const filteredNavigationItems = headerNavigationItems.filter((item) => item.key !== 'estate-karte');

  return (
    <Box className="flex max-xl:hidden">
      {filteredNavigationItems.map((menuItem, index) => (
        <Box key={menuItem.key}>
          <Button
            className={`text-black px-5 py-2.5 rf-hover-text ${isActive(menuItem.href) ? 'active-link' : ''}`}
            sx={{
              zIndex: 1301,
              '&.active-link': {
                textDecoration: 'underline',
                textDecorationThickness: '2px',
                textUnderlineOffset: '16px',
              },
            }}
            onClick={(event) => handleClick(event, index)}
            onMouseEnter={(event) => {
              handlePopoverOpen(event, index);
              setIsHovered(true);
            }}
            onMouseLeave={() => {
              setIsHovered(false);
            }}
          >
            {menuItem.label}
          </Button>
          {menuItem.children && (
            <Popover
              open={currentMenuIndex === index}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <List
                component="nav"
                aria-labelledby={`basic-button-${index}`}
                sx={{ borderRadius: 1, bgColor: '#f7f7f7' }}
                onMouseEnter={() => setIsListHovered(true)}
                onMouseLeave={() => {
                  setIsListHovered(false);
                }}
              >
                {menuItem.children.map((childItem) => (
                  <ListItem key={childItem.key}>
                    <Link href={childItem.href} underline="none" color="inherit" className="text-sm">
                      {childItem.label}
                    </Link>
                  </ListItem>
                ))}
              </List>
            </Popover>
          )}
        </Box>
      ))}
    </Box>
  );
}
